import React, { useEffect, useState, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Download } from 'lucide-react';
import { downloadService } from '../services/downloadService';

interface ImagePreviewModalProps {
  src: string;
  alt?: string;
  title?: string;
  subtitle?: string;
  onClose: () => void;
  onDownload?: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  src,
  alt = '',
  title,
  subtitle,
  onClose,
  onDownload,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);

  const handleZoomIn = useCallback(() => setZoomLevel((p) => Math.min(3, p + 0.25)), []);
  const handleZoomOut = useCallback(() => setZoomLevel((p) => Math.max(0.5, p - 0.25)), []);
  const handleRotate = useCallback(() => setRotation((p) => (p + 90) % 360), []);

  const handleDownloadImage = useCallback(() => {
    if (onDownload) {
      onDownload();
      return;
    }
    void downloadService
      .downloadFileFromUrl(src, title?.replace(/\s+/g, '-') || 'image')
      .catch((error) => console.error('Preview download failed:', error));
  }, [src, title, onDownload]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
        case '_':
          handleZoomOut();
          break;
        case 'r':
        case 'R':
          handleRotate();
          break;
        case 'd':
        case 'D':
          handleDownloadImage();
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, handleZoomIn, handleZoomOut, handleRotate, handleDownloadImage]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <div className="relative max-h-[90vh] max-w-[90vw]">
        {/* Close */}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 backdrop-blur-sm transition-colors hover:bg-white/20"
        >
          <X className="h-6 w-6 text-white" />
        </button>

        {/* Image */}
        <div
          className="flex max-h-[90vh] max-w-[90vw] items-center justify-center overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={src}
            alt={alt}
            className="max-h-full max-w-full object-contain transition-transform duration-300"
            style={{
              transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
              cursor: zoomLevel > 1 ? 'move' : 'default',
            }}
          />
        </div>

        {/* Info */}
        {title && (
          <div className="absolute left-4 top-4 rounded-lg bg-white/10 px-4 py-2 backdrop-blur-md">
            <h3 className="font-semibold text-white">{title}</h3>
            {subtitle && <p className="text-sm text-white/80">{subtitle}</p>}
          </div>
        )}

        {/* Controls */}
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-4 rounded-full bg-white/10 px-4 py-2 backdrop-blur-md">
          <button onClick={(e) => { e.stopPropagation(); handleZoomOut(); }} className="rounded-full p-2 transition-colors hover:bg-white/20" title="Zoom Out">
            <ZoomOut className="h-5 w-5 text-white" />
          </button>
          <span className="min-w-[60px] text-center text-sm text-white">{Math.round(zoomLevel * 100)}%</span>
          <button onClick={(e) => { e.stopPropagation(); handleZoomIn(); }} className="rounded-full p-2 transition-colors hover:bg-white/20" title="Zoom In">
            <ZoomIn className="h-5 w-5 text-white" />
          </button>
          <div className="h-6 w-px bg-white/30" />
          <button onClick={(e) => { e.stopPropagation(); handleRotate(); }} className="rounded-full p-2 transition-colors hover:bg-white/20" title="Rotate">
            <RotateCw className="h-5 w-5 text-white" />
          </button>
          <div className="h-6 w-px bg-white/30" />
          <button onClick={(e) => { e.stopPropagation(); handleDownloadImage(); }} className="rounded-full p-2 transition-colors hover:bg-white/20" title="Download">
            <Download className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Keyboard hints */}
        <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-lg bg-white/10 px-4 py-2 text-sm text-white/80 backdrop-blur-md">
          <kbd className="rounded bg-white/20 px-2 py-1">ESC</kbd> Close{' '}
          <kbd className="ml-2 rounded bg-white/20 px-2 py-1">+/-</kbd> Zoom{' '}
          <kbd className="ml-2 rounded bg-white/20 px-2 py-1">R</kbd> Rotate{' '}
          <kbd className="ml-2 rounded bg-white/20 px-2 py-1">D</kbd> Download
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
